import { useEffect, useCallback } from 'react';
import { useRootStore } from '@/stores/root-store';
import { socketService } from '@/services/socket';
import type { Socket } from 'socket.io-client';

// Types based on Zazu BE Chat documentation
export interface BotSubscription {
  channel: 'telegram' | 'whatsapp';
  botId: string;
}

export interface SendMessagePayload {
  channel: 'telegram' | 'whatsapp';
  contactId: string;
  botId: string;
  message: string;
  messageType?: 'text';
}

export interface NewMessageData {
  botId: string;
  channel: string;
  contactId: string;
  from: string;
  isAdminNotification?: boolean;
  message: {
    from: string;
    id: string;
    text: string;
    timestamp: string;
    type?: 'incoming_message' | 'outgoing_message';
  };
  organizationCode: string;
  roomType: string;
  timestamp: string;
  type?: 'incoming_message' | 'outgoing_message';
}

export interface SendPulseMessageData {
  id: string;
  channel: string;
  contact_id: string;
  bot_id: string;
  direction: number; // 1 = incoming, 2 = outgoing
  status: string; // 'sent', 'delivered', 'read', 'failed', etc.
  created_at: string;
  type: string;
  is_paid: boolean;
  reject_reason: string | null;
  data: {
    message_id: string;
    type: string;
    text?: string;
    preview_url?: string | null;
  };
}

export interface MessageDetails {
  id: string;
  whatsappMessageId?: string;
  direction: number;
  status: string;
  createdAt: string;
  type: string;
  isPaid: boolean;
  rejectReason: string | null;
  channel: string;
  dataType?: string;
  previewUrl?: string | null;
}

export interface NewMessageEvent {
  type: 'incoming_message' | 'outgoing_message';
  channel: string;
  contactId: string;
  botId: string;
  organizationCode: string;
  message: {
    text: string;
    originalText?: string | null;
    from?: string;
    id?: string;
    timestamp?: string;
  };
  messageType: string;
  timestamp: string;
  from: string; // 'sendpulse' para incoming, socket.id para outgoing
  roomType?: 'organization_admin';
  sendPulseData?: SendPulseMessageData; // Datos completos de SendPulse
  messageDetails?: MessageDetails; // Campos extraídos para fácil acceso
}

export interface AuthenticatedData {
  message: string;
  timestamp: string;
  organizationCode: string;
  user: any;
}

export interface SubscriptionConfirmedData {
  channel: string;
  botId: string;
  organizationCode: string;
  subscriptionType: string;
  botRoomId: string;
}

export interface MarkChatAsReadPayload {
  channel: string;
  contactId: string;
}

export interface MarkedChatAsReadResponse {
  chatId: string;
  contactId: string;
  channel: string;
  timestamp: string;
}

export interface MarkReadErrorResponse {
  contactId: string;
  channel: string;
  error: string;
  timestamp: string;
}

const useSocket = ({ enabled }: { enabled: boolean }) => {
  const token = useRootStore((state) => state.token);
  const setIsConnected = useRootStore((state) => state.setIsConnected);
  const setEvent = useRootStore((state) => state.setEvent);
  const pushMessageSent = useRootStore((state) => state.pushMessageSent);
  const pushNewMessage = useRootStore((state) => state.pushNewMessage);

  // Socket connection management
  const connect = useCallback(() => {
    if (!token || !enabled) return null;
    return socketService.connect(token);
  }, [token]);

  const disconnect = useCallback(() => {
    socketService.disconnect();
    setIsConnected(false);
  }, [setIsConnected]);

  const getSocket = useCallback((): Socket | null => {
    return socketService.getSocket();
  }, []);

  // Event emitters (Client → Server)
  const subscribeToBotMessages = useCallback(
    (payload: BotSubscription) => {
      const socket = getSocket();
      if (!socket) return;

      socket.emit('subscribeToBotMessages', payload);
    },
    [getSocket]
  );

  const unsubscribeFromBotMessages = useCallback(
    (botId: string) => {
      const socket = getSocket();
      if (!socket) return;

      socket.emit('unsubscribeFromBotMessages', { botId });
    },
    [getSocket]
  );

  const sendMessage = useCallback(
    (payload: SendMessagePayload) => {
      const socket = getSocket();
      if (!socket) return;
      socket.emit('sendMessage', payload);
    },
    [getSocket]
  );

  const markChatAsRead = useCallback(
    (payload: MarkChatAsReadPayload) => {
      const socket = getSocket();
      if (!socket) return;
      socket.emit('markChatAsRead', payload);
    },
    [getSocket]
  );

  const listAvailableBots = useCallback(() => {
    const socket = getSocket();
    if (!socket) return;

    socket.emit('listAvailableBots');
  }, [getSocket]);

  const ping = useCallback(
    (data?: { timestamp?: number; [key: string]: any }) => {
      const socket = getSocket();
      if (!socket) return;

      socket.emit('ping', { timestamp: Date.now(), ...data });
    },
    [getSocket]
  );

  // Event listeners setup
  const setupEventListeners = useCallback(
    (socket: Socket) => {
      // Connection events
      const handleConnect = () => {
        console.log('✅ Socket connected successfully');
        setIsConnected(true);
        socket.emit('event', 'connected');
      };

      const handleDisconnect = (reason: string) => {
        console.log('🔌 Socket disconnected:', reason);
        setIsConnected(false);
      };

      const handleConnectError = (error: any) => {
        console.error('❌ Socket connection failed:', error);
        setIsConnected(false);
      };

      const handleReconnectAttempt = (attempt: number) => {
        console.log(`🔄 Reconnecting... attempt ${attempt}`);
      };

      const handleReconnectError = (error: any) => {
        console.error('🔥 Reconnection failed:', error);
      };

      const handleReconnectFailed = () => {
        console.error('💥 All reconnection attempts failed');
      };

      // Authentication events
      const handleAuthenticated = (data: AuthenticatedData) => {
        console.log(
          '🔐 Socket authenticated for organization:',
          data.organizationCode
        );
        setEvent(`authenticated_${data.organizationCode}`);
      };

      const handleAuthError = (error: any) => {
        console.error('🚫 Authentication failed:', error.message || error);
        setIsConnected(false);
        setEvent(`auth_error_${error.code}`);
      };

      // Message events
      const handleNewMessage = (data: NewMessageEvent) => {
        console.log('📨 New message received:', data);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pushNewMessage(data);
        setEvent(`new_message_${data.contactId}`);
      };

      // Subscription events
      const handleSubscriptionConfirmed = (data: SubscriptionConfirmedData) => {
        setEvent(`subscription_confirmed_${data.botId}`);
      };

      const handleUnsubscriptionConfirmed = (data: { botId: string }) => {
        setEvent(`unsubscription_confirmed_${data.botId}`);
      };

      // Message status events
      const handleMessageSent = (data: any) => {
        console.log('📤 Message sent successfully:', data);
        pushMessageSent(data, 'right');
        setEvent(`message_sent_${data.messageId}`);
      };

      const handleMessageError = (data: { error: string }) => {
        console.error('📤 Message send failed:', data.error);
        setEvent(`message_error`);
      };

      // Ping/Pong events
      const handlePong = (data: any) => {
        setEvent('pong_received');
      };

      // Generic event handler
      const handleEvent = (event: string) => {
        setEvent(event);
      };

      // markChatAsRead event
      const handleMarkChatAsRead = (data: MarkChatAsReadPayload) => {
        console.log('📖 Try to mark chat as red:', data);
        setEvent(`chat_read_${data.contactId}`);
      };

      // Mark chat as read
      const handleChatMarkedAsRead = (data: MarkedChatAsReadResponse) => {
        console.log('📖 Chat marked as read:', data);
        setEvent(`chat_marked_as_read_${data.contactId}`);
      };

      const handleMarkReadError = (data: MarkReadErrorResponse) => {
        console.error('📖 Chat mark as read failed:', data);
        setEvent(`mark_read_error`);
      };

      // Register all event listeners
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('connect_error', handleConnectError);
      socket.on('reconnect_attempt', handleReconnectAttempt);
      socket.on('reconnect_error', handleReconnectError);
      socket.on('reconnect_failed', handleReconnectFailed);
      socket.on('authenticated', handleAuthenticated);
      socket.on('authError', handleAuthError);
      socket.on('newMessage', handleNewMessage);
      socket.on('subscriptionConfirmed', handleSubscriptionConfirmed);
      socket.on('unsubscriptionConfirmed', handleUnsubscriptionConfirmed);
      socket.on('messageSent', handleMessageSent);
      socket.on('messageError', handleMessageError);
      socket.on('pong', handlePong);
      socket.on('event', handleEvent);
      socket.on('markChatAsRead', handleMarkChatAsRead);
      socket.on('chatMarkedAsRead', handleChatMarkedAsRead);
      socket.on('markReadError', handleMarkReadError);

      // Return cleanup function
      return () => {
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        socket.off('connect_error', handleConnectError);
        socket.off('reconnect_attempt', handleReconnectAttempt);
        socket.off('reconnect_error', handleReconnectError);
        socket.off('reconnect_failed', handleReconnectFailed);
        socket.off('authenticated', handleAuthenticated);
        socket.off('authError', handleAuthError);
        socket.off('newMessage', handleNewMessage);
        socket.off('subscriptionConfirmed', handleSubscriptionConfirmed);
        socket.off('unsubscriptionConfirmed', handleUnsubscriptionConfirmed);
        socket.off('messageSent', handleMessageSent);
        socket.off('messageError', handleMessageError);
        socket.off('pong', handlePong);
        socket.off('event', handleEvent);
        socket.off('markChatAsRead', handleMarkChatAsRead);
        socket.off('chatMarkedAsRead', handleChatMarkedAsRead);
        socket.off('markReadError', handleMarkReadError);
      };
    },
    [setIsConnected, setEvent]
  );

  // Auto-connect effect
  useEffect(() => {
    if (!token || !enabled) return;

    const socket = connect();
    if (!socket) return;

    const cleanup = setupEventListeners(socket);

    return cleanup;
  }, [token, connect, setupEventListeners, enabled]);

  return {
    // Connection management
    connect,
    disconnect,
    getSocket,

    // Event emitters
    subscribeToBotMessages,
    unsubscribeFromBotMessages,
    sendMessage,
    listAvailableBots,
    ping,
    markChatAsRead,

    // State
    isConnected: useRootStore((state) => state.isConnected),
    events: useRootStore((state) => state.events)
  };
};

export default useSocket;
